import React, { useState, useContext, useEffect } from "react";
import VariableContext from "../contexts/VariableContext";
import FetchingFreq from "../contexts/FetchingFreq";
import "./styles/ManageCrossBreaks.css";
import apiService from "../services/apiService";
import { useToast } from "../contexts/ToastContext";
import { useConfirm } from "../contexts/ConfirmContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faPlusCircle,
  faTimes,
  faGripVertical,
  faTrash,
  faPaste,
  faSave,
  faDownload,
  faCalculator,
} from "@fortawesome/free-solid-svg-icons";
import Loader from "./Loader";

function Banner() {
  const { dataGroup, projectId, banners, setBanners } =
    useContext(VariableContext);
  const {
    activePage,
    setActivePage,
    selectedBannerIndex,
    setSelectedBannerIndex,
    isTotal,
    setIsTotal,
    loading,
    setLoading,
  } = useContext(FetchingFreq);
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const [sigLetters, setSigLetters] = useState(
    banners && banners[selectedBannerIndex]
      ? banners[selectedBannerIndex].sigcompare
      : ""
  );
  const [sigTestLevels, setSigTestLevels] = useState(
    banners && banners[selectedBannerIndex]
      ? banners[selectedBannerIndex].sigtestlevel || [95]
      : [95]
  );
  const [sigSymbolScheme, setSigSymbolScheme] = useState(
    banners && banners[selectedBannerIndex]
      ? banners[selectedBannerIndex].sigsymbolscheme || "caps"
      : "caps"
  );
  const [customSigLevel, setCustomSigLevel] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [pasteMode, setPasteMode] = useState(false);
  const [pasteData, setPasteData] = useState("");
  const [draggedRow, setDraggedRow] = useState(null);
  const [countLoadingAll, setCountLoadingAll] = useState(false);
  const [countLoadingRows, setCountLoadingRows] = useState(new Set());
  const [countLoadingAllBanners, setCountLoadingAllBanners] = useState(false);

  useEffect(() => {
    setActivePage("Banner");
  }, [setActivePage]);

  useEffect(() => {
    if (banners && banners[selectedBannerIndex]) {
      setSigLetters(banners[selectedBannerIndex].sigcompare || "");
      setSigTestLevels(banners[selectedBannerIndex].sigtestlevel || [95]);
      setSigSymbolScheme(banners[selectedBannerIndex].sigsymbolscheme || "caps");
    }
  }, [selectedBannerIndex, banners]);

  // ─── Count calculation ────────────────────────────────────────────────────

  const _normaliseLogic = (logic) => (logic || 'ALL').trim();

  const calculateAllCounts = async () => {
    if (!banners || selectedBannerIndex === null) return;
    const selectedBanner = banners[selectedBannerIndex];
    const logics = selectedBanner.rows.map((row) => ({
      logic: _normaliseLogic(row.logic),
      context: `${row.headerText || ''} - ${row.text || ''}`,
    }));
    setCountLoadingAll(true);
    try {
      const resp = await apiService.post('/banner/calculate_counts', {
        project_id: projectId,
        logics,
      });
      const countsMap = resp.data?.counts || {};
      // Use direct-value form (consistent with the rest of the codebase) so
      // React doesn't lose this update when it batches with setCountLoadingAll.
      const updatedBanners = banners.map((banner, bIdx) => {
        if (bIdx !== selectedBannerIndex) return banner;
        return {
          ...banner,
          rows: banner.rows.map((row) => ({
            ...row,
            count: countsMap[_normaliseLogic(row.logic)],
          })),
        };
      });
      setBanners(updatedBanners);
    } catch (err) {
      console.error('Calculate counts failed:', err);
      toast.error('Failed to calculate counts.');
    } finally {
      setCountLoadingAll(false);
    }
  };

  const calculateAllBannersCounts = async () => {
    if (!banners || banners.length === 0) return;
    const logics = [];
    banners.forEach((banner) => {
      (banner.rows || []).forEach((row) => {
        logics.push({
          logic: _normaliseLogic(row.logic),
          context: `${banner.name || ''} - ${row.headerText || ''} - ${row.text || ''}`,
        });
      });
    });
    setCountLoadingAllBanners(true);
    try {
      const resp = await apiService.post('/banner/calculate_counts', {
        project_id: projectId,
        logics,
      });
      const countsMap = resp.data?.counts || {};
      const updatedBanners = banners.map((banner) => ({
        ...banner,
        rows: banner.rows.map((row) => ({
          ...row,
          count: countsMap[_normaliseLogic(row.logic)],
        })),
      }));
      setBanners(updatedBanners);
    } catch (err) {
      console.error('Calculate all-banners counts failed:', err);
      toast.error('Failed to calculate counts for all banners.');
    } finally {
      setCountLoadingAllBanners(false);
    }
  };

  const calculateSingleCount = async (logic, context) => {
    const key = _normaliseLogic(logic);
    setCountLoadingRows((prev) => new Set([...prev, key]));
    try {
      const resp = await apiService.post('/banner/calculate_counts', {
        project_id: projectId,
        logics: [{ logic: key, context }],
      });
      const count = (resp.data?.counts || {})[key];
      setBanners((prev) =>
        prev.map((banner, bIdx) => {
          if (bIdx !== selectedBannerIndex) return banner;
          return {
            ...banner,
            rows: banner.rows.map((row) =>
              _normaliseLogic(row.logic) === key ? { ...row, count } : row
            ),
          };
        })
      );
    } catch (err) {
      console.error('Calculate single count failed:', err);
      toast.error('Failed to calculate count.');
    } finally {
      setCountLoadingRows((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  // ─── Letter helpers ────────────────────────────────────────────────────────

  const getAlphabeticIdentifier = (index) => {
    let identifier = "";
    while (index >= 0) {
      identifier = String.fromCharCode((index % 26) + 65) + identifier;
      index = Math.floor(index / 26) - 1;
    }
    return identifier;
  };

  // Continuous letters across ALL rows (default behaviour)
  const assignContinuousLetters = (rows) =>
    rows.map((row, index) => ({ ...row, letter: getAlphabeticIdentifier(index) }));

  // Keep assignLetters as an alias so every existing call still works
  const assignLetters = (rows) => assignContinuousLetters(rows);

  // ─── Row / banner mutations ────────────────────────────────────────────────

  const removeRowFromBanner = (bannerIndex, rowIndex) => {
    const updatedBanners = banners.map((banner, index) => {
      if (index === bannerIndex) {
        const filtered = banner.rows.filter((_, rIndex) => rIndex !== rowIndex);
        return { ...banner, rows: assignContinuousLetters(filtered) };
      }
      return banner;
    });
    setBanners(updatedBanners);
  };

  // ─── GROUP TOTAL helpers ───────────────────────────────────────────────────

  /**
   * Add (or refresh) a Total row at the beginning of the given group.
   * Logic = OR of every non-total row's logic in that group.
   * If a Total row already exists it is removed first so the logic is fresh.
   */
  const addTotalToGroup = (bannerIndex, headerText) => {
    const updatedBanners = banners.map((banner, index) => {
      if (index === bannerIndex) {
        // Remove any existing total rows for this group
        let rows = banner.rows.filter(
          (r) => !(r.headerText === headerText && r.isGroupTotal)
        );

        // Build OR logic from every non-total row in this group
        const groupRows = rows.filter((r) => r.headerText === headerText);
        const orLogic = groupRows
          .map((r) => (r.logic || "").trim())
          .filter((l) => l !== "")
          .join(" OR ");

        // Insert the Total row at the very beginning of this group
        const newRows = [];
        let inserted = false;
        for (const row of rows) {
          if (row.headerText === headerText && !inserted) {
            newRows.push({
              headerText,
              text: "Total",
              logic: orLogic,
              isGroupTotal: true,
            });
            inserted = true;
          }
          newRows.push(row);
        }

        return { ...banner, rows: assignContinuousLetters(newRows) };
      }
      return banner;
    });
    setBanners(updatedBanners);
  };

  /**
   * Remove the Total row from a group.
   */
  const removeTotalFromGroup = (bannerIndex, headerText) => {
    const updatedBanners = banners.map((banner, index) => {
      if (index === bannerIndex) {
        const rows = banner.rows.filter(
          (r) => !(r.headerText === headerText && r.isGroupTotal)
        );
        return { ...banner, rows: assignContinuousLetters(rows) };
      }
      return banner;
    });
    setBanners(updatedBanners);
  };

  /**
   * Add (or refresh) a Total row for EVERY group in the banner.
   */
  const addTotalToAllGroups = (bannerIndex) => {
    const updatedBanners = banners.map((banner, index) => {
      if (index !== bannerIndex) return banner;

      // Collect unique group keys in their original order
      const seenKeys = [];
      banner.rows.forEach((r) => {
        if (!seenKeys.includes(r.headerText)) seenKeys.push(r.headerText);
      });

      let rows = banner.rows;

      seenKeys.forEach((headerText) => {
        // Remove any existing total for this group
        rows = rows.filter(
          (r) => !(r.headerText === headerText && r.isGroupTotal)
        );

        // Build OR logic from every non-total row in this group
        const groupRows = rows.filter((r) => r.headerText === headerText);
        const orLogic = groupRows
          .map((r) => (r.logic || "").trim())
          .filter((l) => l !== "")
          .join(" OR ");

        // Insert Total at the start of this group
        const newRows = [];
        let inserted = false;
        for (const row of rows) {
          if (row.headerText === headerText && !inserted) {
            newRows.push({
              headerText,
              text: "Total",
              logic: orLogic,
              isGroupTotal: true,
            });
            inserted = true;
          }
          newRows.push(row);
        }
        rows = newRows;
      });

      return { ...banner, rows: assignContinuousLetters(rows) };
    });
    setBanners(updatedBanners);
  };

  /**
   * Remove Total rows from EVERY group in the banner.
   */
  const removeTotalFromAllGroups = (bannerIndex) => {
    const updatedBanners = banners.map((banner, index) => {
      if (index !== bannerIndex) return banner;
      const rows = banner.rows.filter((r) => !r.isGroupTotal);
      return { ...banner, rows: assignContinuousLetters(rows) };
    });
    setBanners(updatedBanners);
  };

  // ─── Drag-and-drop ────────────────────────────────────────────────────────

  const handleDragStart = (e, bannerIndex, rowIndex) => {
    e.dataTransfer.setData(
      "text/plain",
      JSON.stringify({ bannerIndex, rowIndex })
    );
    e.currentTarget.classList.add("dragging");
    setDraggedRow(rowIndex);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add("drag-over");
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove("drag-over");
  };

  const handleDrop = (e, targetBannerIndex, targetRowIndex) => {
    e.preventDefault();
    e.currentTarget.classList.remove("drag-over");

    const draggedData = JSON.parse(e.dataTransfer.getData("text/plain"));
    const { bannerIndex: sourceBannerIndex, rowIndex: sourceRowIndex } =
      draggedData;

    if (
      sourceBannerIndex === targetBannerIndex &&
      sourceRowIndex !== targetRowIndex
    ) {
      const updatedBanners = banners.map((banner, index) => {
        if (index === targetBannerIndex) {
          const newRows = [...banner.rows];
          const [draggedItem] = newRows.splice(sourceRowIndex, 1);

          let newPosition = targetRowIndex;
          if (sourceRowIndex < targetRowIndex) {
            newPosition = targetRowIndex - 1;
          }

          newRows.splice(newPosition, 0, draggedItem);

          return { ...banner, rows: assignContinuousLetters(newRows) };
        }
        return banner;
      });

      setBanners(updatedBanners);
    }

    document.querySelectorAll(".dragging, .drag-over").forEach((el) => {
      el.classList.remove("dragging", "drag-over");
    });
    setDraggedRow(null);
  };

  const removeAllRowsForVariable = (bannerIndex, headerText) => {
    const updatedBanners = banners.map((banner, index) => {
      if (index === bannerIndex) {
        const filtered = banner.rows.filter((row) => row.headerText !== headerText);
        return { ...banner, rows: assignContinuousLetters(filtered) };
      }
      return banner;
    });
    setBanners(updatedBanners);
  };

  // ─── Banner-level mutations ────────────────────────────────────────────────

  const deleteBanner = async (bannerIndex) => {
    const bannerName = banners[bannerIndex].name || `Banner ${bannerIndex + 1}`;
    const ok = await confirm({
      title: "Delete Banner",
      message: `Are you sure you want to delete "${bannerName}"? This action cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      variant: "danger",
    });
    if (!ok) return;

    const updatedBanners = banners.filter((_, index) => index !== bannerIndex);
    setBanners(updatedBanners);
    if (selectedBannerIndex === bannerIndex) {
      if (updatedBanners.length === 0) {
        setSelectedBannerIndex(null);
      } else if (selectedBannerIndex >= updatedBanners.length) {
        setSelectedBannerIndex(updatedBanners.length - 1);
      }
    }
  };

  const selectBanner = (bannerIndex) => {
    setSelectedBannerIndex(bannerIndex);
  };

  const renderTabs = () => {
    if (banners) {
      return banners.map((banner, index) => (
        <div key={index} className="banner-tab-wrapper">
          <button
            onClick={() => selectBanner(index)}
            className={selectedBannerIndex === index ? "active" : ""}
          >
            {banner.name || `Banner ${index + 1}`}
          </button>
          <button
            className="delete-banner-btn"
            onClick={() => deleteBanner(index)}
            title={`Delete ${banner.name || `Banner ${index + 1}`}`}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      ));
    }
  };

  const addBanner = () => {
    const safeBanners = Array.isArray(banners) ? banners : [];
    const newIndex = safeBanners.length + 1;
    const newBanner = {
      name: `Banner ${newIndex}`,
      rows: [{ headerText: "Total", text: "Total", letter: "A", logic: "ALL" }],
      minbase: "",
      lowbase: "",
      minbaseSymbol: "**",
      lowbaseSymbol: "*",
      isCompliment: false,
      sigtestlevel: [95],
      sigsymbolscheme: "caps",
    };
    setBanners([...safeBanners, newBanner]);
    setSelectedBannerIndex(safeBanners.length);
  };

  // ─── Field-level change handlers (unchanged) ──────────────────────────────

  const handleBannerNameChange = (bannerIndex, newName) => {
    const updatedBanners = banners.map((banner, index) => {
      if (index === bannerIndex) {
        return { ...banner, name: newName };
      }
      return banner;
    });
    setBanners(updatedBanners);
  };

  const handleMinBaseChange = (bannerIndex, value) => {
    const updatedBanners = banners.map((banner, index) => {
      if (index === bannerIndex) {
        return { ...banner, minbase: value };
      }
      return banner;
    });
    setBanners(updatedBanners);
  };

  const handleMinBaseSymbolChange = (bannerIndex, value) => {
    const updatedBanners = banners.map((banner, index) => {
      if (index === bannerIndex) {
        return { ...banner, minbaseSymbol: value };
      }
      return banner;
    });
    setBanners(updatedBanners);
  };

  const handleIsComplimentChange = (bannerIndex, value) => {
    const updatedBanners = banners.map((banner, index) => {
      if (index === bannerIndex) {
        return { ...banner, isCompliment: value };
      }
      return banner;
    });
    setBanners(updatedBanners);
  };


  const handleLowBaseChange = (bannerIndex, value) => {
    const updatedBanners = banners.map((banner, index) => {
      if (index === bannerIndex) {
        return { ...banner, lowbase: value };
      }
      return banner;
    });
    setBanners(updatedBanners);
  };

  const handleLowBaseSymbolChange = (bannerIndex, value) => {
    const updatedBanners = banners.map((banner, index) => {
      if (index === bannerIndex) {
        return { ...banner, lowbaseSymbol: value };
      }
      return banner;
    });
    setBanners(updatedBanners);
  };

  const addRowToBanner = (bannerIndex) => {
    const updatedBanners = banners.map((banner, index) => {
      if (index === bannerIndex) {
        const newRows = [...banner.rows, { headerText: "", text: "", logic: "" }];
        return { ...banner, rows: assignContinuousLetters(newRows) };
      }
      return banner;
    });
    setBanners(updatedBanners);
  };

  const handleRowChange = (bannerIndex, rowIndex, key, value) => {
    const updatedBanners = banners.map((banner, bIndex) => {
      if (bIndex === bannerIndex) {
        const updatedRows = banner.rows.map((row, rIndex) => {
          if (rIndex === rowIndex) {
            return { ...row, [key]: value };
          }
          return row;
        });
        return { ...banner, rows: updatedRows };
      }
      return banner;
    });
    setBanners(updatedBanners);
  };

  // ─── TextWithIdentifier component (unchanged) ────────────────────────────

  const TextWithIdentifier = ({ value, identifier, onChange }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [inputValue, setInputValue] = useState(value);

    const handleChange = (e) => {
      setInputValue(e.target.value);
    };

    const handleBlur = () => {
      onChange(inputValue);
      setIsEditing(false);
    };

    return (
      <input
        type="text"
        value={isEditing ? inputValue : `${inputValue} [${identifier}]`}
        onChange={handleChange}
        onFocus={() => setIsEditing(true)}
        onBlur={handleBlur}
      />
    );
  };

  // ─── Sig letters / levels ─────────────────────────────────────────────────

  const updateLetters = (bannerIndex, newletters) => {
    setSigLetters(newletters);
    const updatedBanners = banners.map((banner, index) => {
      if (index === bannerIndex) {
        return { ...banner, sigcompare: newletters };
      }
      return banner;
    });
    setBanners(updatedBanners);
  };

  const handleSigLevelToggle = (level) => {
    const current = banners[selectedBannerIndex].sigtestlevel || [95];
    const newLevels = current.includes(level)
      ? current.filter((l) => l !== level)
      : [...current, level];
    const updated = banners.map((banner, index) =>
      index === selectedBannerIndex
        ? { ...banner, sigtestlevel: newLevels }
        : banner
    );
    setBanners(updated);
    setSigTestLevels(newLevels);
  };

  const handleCustomSigLevelAdd = () => {
    if (!customSigLevel.trim()) return;
    const parsed = customSigLevel
      .split(";")
      .map((v) => parseInt(v.trim(), 10))
      .filter((v) => !isNaN(v) && v > 0 && v < 100);
    if (parsed.length === 0) return;
    const current = banners[selectedBannerIndex].sigtestlevel || [95];
    const merged = [...new Set([...current, ...parsed])];
    const updated = banners.map((banner, index) =>
      index === selectedBannerIndex
        ? { ...banner, sigtestlevel: merged }
        : banner
    );
    setBanners(updated);
    setSigTestLevels(merged);
    setCustomSigLevel("");
  };

  const handleSigSchemeChange = (scheme) => {
    const updated = banners.map((banner, index) =>
      index === selectedBannerIndex
        ? { ...banner, sigsymbolscheme: scheme }
        : banner
    );
    setBanners(updated);
    setSigSymbolScheme(scheme);
  };

  /**
   * Generate the sig-test comparison string using each row's own globally
   * continuous letter (A, B, C, D, E, F… never restarting) — this is the
   * same letter shown on the row and used as the column header at export/run
   * time, so it must stay globally unique. Groups (by headerText) are still
   * visually separated with " / " in the comparison string so sig-testing
   * stays scoped to each group, e.g. "A, B, C / D, E, F, G / H, I".
   */
  const generateSigLetters = () => {
    if (!banners || banners.length === 0 || !banners[selectedBannerIndex])
      return "";

    let letternew = "";
    let prevHeader = null;

    const updatedBanners = banners.map((banner, bannerIndex) => {
      if (bannerIndex === selectedBannerIndex) {
        const updatedRows = banner.rows.map((row, index) => {
          const letter = getAlphabeticIdentifier(index);

          if (prevHeader !== null && row.headerText !== prevHeader) {
            letternew += " / ";
          } else if (letternew) {
            letternew += ", ";
          }
          letternew += letter;
          prevHeader = row.headerText;

          return { ...row, letter };
        });

        return { ...banner, rows: updatedRows, sigcompare: letternew };
      }
      return banner;
    });

    setBanners(updatedBanners);
    setSigLetters(letternew);
    return letternew;
  };

  // ─── Paste handler ────────────────────────────────────────────────────────

  const handlePasteExcelData = () => {
    if (!pasteData.trim()) return;

    try {
      const rows = pasteData.split("\n").filter((row) => row.trim() !== "");
      const newRows = rows.map((row) => {
        const [headerText = "", text = "", logic = ""] = row
          .split(/\t|,/)
          .map((item) => item.trim());
        return { headerText, text, logic };
      });

      const updatedBanners = banners.map((banner, index) => {
        if (index === selectedBannerIndex) {
          return {
            ...banner,
            rows: assignContinuousLetters([...banner.rows, ...newRows]),
          };
        }
        return banner;
      });

      setBanners(updatedBanners);
      setPasteData("");
      setPasteMode(false);
    } catch (error) {
      console.error("Error processing pasted data:", error);
      toast.error("Error processing pasted data. Please ensure it is in the correct format.");
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  const renderSelectedBanner = () => {
    if (banners && selectedBannerIndex !== null) {
      const selectedBanner = banners[selectedBannerIndex];

      // Build groupedRows map (used to detect "first row of group")
      const groupedRows = {};
      selectedBanner.rows.forEach((row, index) => {
        if (!groupedRows[row.headerText]) {
          groupedRows[row.headerText] = [];
        }
        groupedRows[row.headerText].push({ ...row, originalIndex: index });
      });

      // Helper: does a group already have a Total row?
      const groupHasTotal = (headerText) =>
        selectedBanner.rows.some(
          (r) => r.headerText === headerText && r.isGroupTotal
        );

      return (
        <div key={selectedBannerIndex}>
          <input
            type="text"
            value={selectedBanner.name}
            onChange={(e) =>
              handleBannerNameChange(selectedBannerIndex, e.target.value)
            }
            placeholder="Enter Banner Name"
          />

          <button className="banner-btn" onClick={() => generateSigLetters()}>
            <FontAwesomeIcon icon={faPlusCircle} /> Generate Sig Test Letters
          </button>

          <button
            className="banner-btn"
            onClick={calculateAllCounts}
            disabled={countLoadingAll}
            title="Calculate respondent counts for all banner points"
          >
            <FontAwesomeIcon icon={faCalculator} />
            {countLoadingAll ? " Calculating..." : " Calculate Counts"}
          </button>

          <button
            className="banner-btn"
            onClick={calculateAllBannersCounts}
            disabled={countLoadingAllBanners}
            title="Calculate respondent counts for every banner point across all banners"
          >
            <FontAwesomeIcon icon={faCalculator} />
            {countLoadingAllBanners ? " Calculating All Banners..." : " Calculate Counts (All Banners)"}
          </button>

          <div>
            <label>Min Base: </label>
            <input
              type="number"
              value={selectedBanner.minbase || ""}
              onChange={(e) =>
                handleMinBaseChange(selectedBannerIndex, e.target.value)
              }
              placeholder="Enter Min Base"
            />
            <input
              type="text"
              value={selectedBanner.minbaseSymbol || "**"}
              onChange={(e) =>
                handleMinBaseSymbolChange(selectedBannerIndex, e.target.value)
              }
              placeholder="Symbol"
              style={{ width: "80px" }}
            />
          </div>

          <div>
            <label>Low Base: </label>
            <input
              type="number"
              value={selectedBanner.lowbase || ""}
              onChange={(e) =>
                handleLowBaseChange(selectedBannerIndex, e.target.value)
              }
              placeholder="Enter Low Base"
            />
            <input
              type="text"
              value={selectedBanner.lowbaseSymbol || "*"}
              onChange={(e) =>
                handleLowBaseSymbolChange(selectedBannerIndex, e.target.value)
              }
              placeholder="Symbol"
              style={{ width: "80px" }}
            />
          </div>

          <div className="toggle-switch">
            <input
              type="checkbox"
              id="toggle-compliment"
              checked={selectedBanner.isCompliment || false}
              onChange={(e) =>
                handleIsComplimentChange(selectedBannerIndex, e.target.checked)
              }
            />
            <label htmlFor="toggle-compliment" className="toggle-label"></label>
            <span className="toggle-text">Compliment Test</span>
          </div>

          <input
            type="text"
            onChange={(e) => updateLetters(selectedBannerIndex, e.target.value)}
            value={sigLetters}
            placeholder="Sig test letters (e.g., A, B, C / D, E, F)"
          />

          <div style={{ marginTop: "12px" }}>
            <label>
              <strong>Sig Test Level(s):</strong>
            </label>
            <div
              style={{
                display: "flex",
                gap: "14px",
                flexWrap: "wrap",
                marginTop: "6px",
              }}
            >
              {[80, 90, 95, 99].map((level) => (
                <label
                  key={level}
                  style={{ display: "flex", alignItems: "center", gap: "4px" }}
                >
                  <input
                    type="checkbox"
                    checked={sigTestLevels.includes(level)}
                    onChange={() => handleSigLevelToggle(level)}
                  />
                  {level}%
                </label>
              ))}
            </div>
            <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
              <input
                type="text"
                value={customSigLevel}
                onChange={(e) => setCustomSigLevel(e.target.value)}
                placeholder="Custom levels, ; separated (e.g. 85;97)"
                style={{ flex: 1 }}
                onKeyDown={(e) => e.key === "Enter" && handleCustomSigLevelAdd()}
              />
              <button type="button" className="banner-btn" onClick={handleCustomSigLevelAdd}>
                Add
              </button>
            </div>
            {sigTestLevels.length > 0 && (
              <div
                style={{ marginTop: "4px", fontSize: "0.85rem", color: "#555" }}
              >
                Active:{" "}
                {[...sigTestLevels]
                  .sort((a, b) => b - a)
                  .map((l) => l + "%")
                  .join(", ")}
              </div>
            )}
          </div>

          <div style={{ marginTop: "12px" }}>
            <label>
              <strong>Sig Symbol Scheme: </strong>
            </label>
            <select
              value={sigSymbolScheme}
              onChange={(e) => handleSigSchemeChange(e.target.value)}
              style={{ marginLeft: "8px" }}
            >
              <option value="caps">Capital Letters – A, B, C (1 level)</option>
              <option value="caps_lower">
                Capital + Lowercase – A / a (2 levels)
              </option>
              <option value="stars3">Stars – *, **, *** (3 levels)</option>
              <option value="stars4">
                Dot + Stars – ., *, **, *** (4 levels)
              </option>
            </select>
          </div>

          <div style={{ fontSize: "0.8rem", color: "#718096", margin: "8px 0" }}>
            Tip: instead of a filter, a column's Logic can be a Column Calc expression referencing other
            columns by 0-based position — e.g. C1+C2+C3, C1-C2, (C1+C2)/C0 — to combine already-computed
            columns in place. Only combine columns whose respondents don't overlap (e.g. adjacent age
            bands), not overlapping ones (e.g. Gender + Age), since this sums counts rather than
            recomputing from respondent IDs.
          </div>

          {pasteMode ? (
            <div className="paste-excel-area">
              <h4>📋 Paste Excel Data</h4>
              <p
                style={{
                  fontSize: "0.875rem",
                  color: "#718096",
                  marginBottom: "12px",
                }}
              >
                Paste tab or comma separated data with format: HeaderText, Text,
                Logic
              </p>
              <textarea
                value={pasteData}
                onChange={(e) => setPasteData(e.target.value)}
                placeholder="Example:&#10;Gender&#9;Male&#9;Q1=1&#10;Gender&#9;Female&#9;Q1=2"
                rows={6}
              />
              <div>
                <button className="banner-btn" onClick={handlePasteExcelData}>
                  <FontAwesomeIcon icon={faPlusCircle} /> Add Rows
                </button>
                <button className="banner-btn banner-btn--outline" onClick={() => setPasteMode(false)}>
                  <FontAwesomeIcon icon={faTimes} /> Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* ── Apply / Remove Total for ALL groups ─────────────────── */}
              <div className="group-total-actions">
                <button
                  className="apply-all-total-btn"
                  onClick={() => addTotalToAllGroups(selectedBannerIndex)}
                  title="Add (or refresh) a Total row for every group"
                >
                  Σ Apply Total to All Groups
                </button>
                <button
                  className="remove-all-total-btn"
                  onClick={() => removeTotalFromAllGroups(selectedBannerIndex)}
                  title="Remove Total rows from every group"
                >
                  <FontAwesomeIcon icon={faTimes} /> Remove All Group Totals
                </button>
              </div>

              <table>
                <thead>
                  <tr>
                    <th style={{ width: "5%" }}>Drag</th>
                    <th style={{ width: "20%" }}>Header Text</th>
                    <th style={{ width: "23%" }}>Text</th>
                    <th style={{ width: "28%" }}>Logic</th>
                    <th style={{ width: "70px", textAlign: "center" }}>
                      Remove
                    </th>
                    <th style={{ width: "70px", textAlign: "center" }}>
                      Delete All
                    </th>
                    <th style={{ width: "90px", textAlign: "center" }}>
                      Group Total
                    </th>
                    <th style={{ width: "90px", textAlign: "center" }}>
                      Count
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {selectedBanner.rows.map((row, rowIndex) => {
                    const isFirstOfGroup =
                      groupedRows[row.headerText]?.[0]?.originalIndex ===
                      rowIndex;
                    const hasTotal = groupHasTotal(row.headerText);

                    return (
                      <tr
                        key={rowIndex}
                        draggable="true"
                        onDragStart={(e) =>
                          handleDragStart(e, selectedBannerIndex, rowIndex)
                        }
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) =>
                          handleDrop(e, selectedBannerIndex, rowIndex)
                        }
                        className={[
                          draggedRow === rowIndex ? "dragging" : "",
                          row.isGroupTotal ? "group-total-row" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        {/* ── Drag handle ── */}
                        <td style={{ textAlign: "center", cursor: "move" }}>
                          <FontAwesomeIcon
                            icon={faGripVertical}
                            className="drag-handle"
                            title="Drag to reorder"
                            style={{ color: "#718096", fontSize: "16px" }}
                          />
                        </td>

                        {/* ── Header Text ── */}
                        <td>
                          <input
                            type="text"
                            value={row.headerText}
                            onChange={(e) =>
                              handleRowChange(
                                selectedBannerIndex,
                                rowIndex,
                                "headerText",
                                e.target.value
                              )
                            }
                            placeholder="e.g., Gender"
                          />
                        </td>

                        {/* ── Text (with group-relative letter from row.letter) ── */}
                        <td>
                          <TextWithIdentifier
                            value={row.text}
                            identifier={
                              row.letter ||
                              getAlphabeticIdentifier(rowIndex)
                            }
                            onChange={(newValue) =>
                              handleRowChange(
                                selectedBannerIndex,
                                rowIndex,
                                "text",
                                newValue
                              )
                            }
                          />
                        </td>

                        {/* ── Logic ── */}
                        <td>
                          <input
                            type="text"
                            value={row.logic}
                            title="A filter expression (e.g. Q1=1), or a Column Calc like C1+C2 to combine other columns by position"
                            onChange={(e) =>
                              handleRowChange(
                                selectedBannerIndex,
                                rowIndex,
                                "logic",
                                e.target.value
                              )
                            }
                            placeholder="e.g., Q1=1"
                          />
                        </td>

                        {/* ── Remove single row ── */}
                        <td style={{ textAlign: "center" }}>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() =>
                              removeRowFromBanner(selectedBannerIndex, rowIndex)
                            }
                            title="Remove row"
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        </td>

                        {/* ── Delete all rows for this group (first row only) ── */}
                        <td style={{ textAlign: "center" }}>
                          {isFirstOfGroup && (
                            <button
                              onClick={() =>
                                removeAllRowsForVariable(
                                  selectedBannerIndex,
                                  row.headerText
                                )
                              }
                              title={`Remove all "${row.headerText}" rows`}
                              className="remove-all-btn"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          )}
                        </td>

                        {/* ── Group Total column ─────────────────────────────
                             • First row of a group with NO total  → "Add Total" button
                             • The Total row itself                 → "Remove Total" button
                             • All other rows                       → empty
                        ─────────────────────────────────────────────────────── */}
                        <td style={{ textAlign: "center" }}>
                          {isFirstOfGroup && !hasTotal && (
                            <button
                              className="add-total-btn"
                              onClick={() =>
                                addTotalToGroup(
                                  selectedBannerIndex,
                                  row.headerText
                                )
                              }
                              title={`Add Total for "${row.headerText}" group`}
                            >
                              Σ Total
                            </button>
                          )}
                          {row.isGroupTotal && (
                            <button
                              className="remove-total-btn"
                              onClick={() =>
                                removeTotalFromGroup(
                                  selectedBannerIndex,
                                  row.headerText
                                )
                              }
                              title={`Remove Total for "${row.headerText}" group`}
                            >
                              <FontAwesomeIcon icon={faTimes} /> Total
                            </button>
                          )}
                        </td>

                        {/* ── Count column ──────────────────────────────────── */}
                        {(() => {
                          const logicKey = _normaliseLogic(row.logic);
                          const isRowLoading = countLoadingRows.has(logicKey);
                          const countVal = row.count;
                          return (
                            <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                              {isRowLoading ? (
                                <span style={{ fontSize: "11px", color: "#718096" }}>…</span>
                              ) : countVal !== undefined ? (
                                <span
                                  style={{
                                    background: countVal === null ? "#fed7d7" : "#c6f6d5",
                                    color: countVal === null ? "#9b2c2c" : "#276749",
                                    borderRadius: "4px",
                                    padding: "2px 6px",
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    marginRight: "4px",
                                  }}
                                >
                                  {countVal === null ? "err" : countVal.toLocaleString()}
                                </span>
                              ) : null}
                              <button
                                title="Calculate count for this banner point"
                                style={{
                                  padding: "2px 5px",
                                  fontSize: "11px",
                                  background: "#edf2f7",
                                  border: "1px solid #cbd5e0",
                                  borderRadius: "3px",
                                  cursor: "pointer",
                                  color: "#4a5568",
                                }}
                                onClick={() =>
                                  calculateSingleCount(
                                    row.logic,
                                    `${row.headerText || ''} - ${row.text || ''}`
                                  )
                                }
                              >
                                <FontAwesomeIcon icon={faCalculator} />
                              </button>
                            </td>
                          );
                        })()}
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div className="add-buttons">
                <button className="banner-btn" onClick={() => addRowToBanner(selectedBannerIndex)}>
                  <FontAwesomeIcon icon={faPlusCircle} /> Add Banner Point
                </button>
                <button className="banner-btn banner-btn--outline" onClick={() => setPasteMode(true)}>
                  <FontAwesomeIcon icon={faPaste} /> Paste Excel Rows
                </button>
              </div>
            </>
          )}
        </div>
      );
    }
    return null;
  };

  // ─── Save ─────────────────────────────────────────────────────────────────

  const saveBannersToBackend = async () => {
    setLoading(true);
    try {
      await apiService.post("/savebanner", {
        banner: JSON.stringify(banners),
        project_id: projectId,
      });
      toast.success("Banner saved successfully!");
    } catch (error) {
      console.error("Error saving banners:", error);
      toast.error("Failed to save. Please contact your administrator.");
    } finally {
      setLoading(false);
    }
  };

  // ─── Download JSON ────────────────────────────────────────────────────────

  const downloadBannersJson = () => {
    const json = JSON.stringify(banners, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `banners_project_${projectId || "export"}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ─── JSX ──────────────────────────────────────────────────────────────────

  return (
    <div className="manage-cross-breaks">
      <div className="workspace">
        {loading ? (
          <Loader loading={loading} />
        ) : (
          <>
            <button className="banner-btn" onClick={addBanner}>
              <FontAwesomeIcon icon={faPlus} /> Add New Banner
            </button>
            <button className="banner-btn banner-btn--success" onClick={saveBannersToBackend}>
              <FontAwesomeIcon icon={faSave} /> Save All Banners
            </button>
            <button className="banner-btn banner-btn--outline download-json-btn" onClick={downloadBannersJson}>
              <FontAwesomeIcon icon={faDownload} /> Download JSON
            </button>

            <div className="tabs">{renderTabs()}</div>

            <div className="selected-banner">{renderSelectedBanner()}</div>

            <button
              className="banner-btn banner-btn--success"
              onClick={saveBannersToBackend}
              style={{ marginTop: "20px" }}
            >
              <FontAwesomeIcon icon={faSave} /> Save All Banners
            </button>
          </>
        )}
      </div>

    </div>
  );
}

export default Banner;
